import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { Textarea } from "src/components/base/textarea";
import { ApiInternal } from "src/api/api_internal";
import { UUID } from "src/api/api_common";

/**
 * The properties for {@link CreateDeviceGroupDialog}
 */
export type CreateDeviceGroupDialogProps = {
    /** Wheter the dialog is open */
    isOpen: boolean;
    /** Callback when the device group was created successfully but before the dialog is closed */
    onSubmit?: (deviceGroupUuid: UUID) => void;
    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to create a new device group
 */
export default function CreateDeviceGroupDialog(props: CreateDeviceGroupDialogProps) {
    const [t] = useTranslation("dialog-create-device-group");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: { name: "", description: "" },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.deviceGroups.create({ name: value.name, description: value.description });

                if (res.result === "Err") {
                    return {
                        fields: {
                            name: res.error ? t("error.name-not-unique") : undefined,
                            description: undefined,
                        },
                    };
                }

                if (props.onSubmit) {
                    props.onSubmit(res.value.uuid);
                }
            },
        },
        onSubmit: () => {
            props.onClose();
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen]);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.create-device-group")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Description>{t("description.name")}</Description>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            pattern={"[a-z0-9]+(-*[a-z0-9]+)*"}
                                            maxLength={255}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Textarea
                                            value={fieldApi.state.value}
                                            maxLength={1024}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            resizable={false}
                                        />
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.create-device-group")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
